import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button, Input, Modal, Search } from '@indipia/ui-core';
import { worklogApi } from '../api/worklogApi';
import { useWorkTypeName } from '../hooks/useWorkTypeName';
import type { Partner } from '../types/worklog.types';

/**
 * 시공파트너 지정 팝업 — 일괄 작업요청(§5.4.2)의 "시공파트너 미지정" 공정 전용.
 * 기존 파트너 검색·선택 또는 "+새 시공파트너 등록"(이름·연락처·전문공종)으로 즉시 등록 후 지정한다.
 */
export function PartnerAssignModal({
  taskLabel,
  onClose,
  onAssign,
}: {
  /** "현장명 · 날짜 · 공종" — 어느 공정에 지정하는지 헤더 아래 표시 */
  taskLabel: string;
  onClose: () => void;
  onAssign: (partner: { partnerId: number; partnerName: string; mobile: string | null }) => void;
}) {
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newRole, setNewRole] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 공종 코드 → 공종명 매핑 (공통코드 ZEU_WORK_TYPE) — PartnerFilterPopover(WorkOrderListPage)와 동일 규칙.
  const workTypeName = useWorkTypeName();
  const specialtyLabel = (specialtyTypes: string | null) =>
    specialtyTypes
      ? specialtyTypes
          .split(',')
          .map((code) => workTypeName.get(code) ?? code)
          .join(', ')
      : '-';

  // 부모가 지정 대상이 있을 때만 마운트 — 열 때마다 새 state로 시작하므로 초기 조회만 한다.
  useEffect(() => {
    void search('');
  }, []);

  const search = async (q: string) => {
    setLoading(true);
    try {
      const res = await worklogApi.getPartners(q || undefined);
      setResults(res.data.data ?? []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const createAndAssign = async () => {
    const name = newName.trim();
    const phone = newPhone.trim();
    if (!name || !phone) {
      toast.warning('이름과 연락처를 입력해주세요');
      return;
    }
    setSubmitting(true);
    try {
      const res = await worklogApi.createPartner({
        partnerName: name,
        mobile: phone,
        specialtyTypes: newRole.trim() || undefined,
      });
      onAssign({ partnerId: res.data.data, partnerName: name, mobile: phone });
    } catch {
      toast.error('시공파트너 등록에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="시공파트너 지정"
      footer={
        <Button variant="etc" onClick={onClose}>
          닫기
        </Button>
      }
    >
      <div className="flex flex-col gap-sm">
        <div className="text-body-sm text-text-secondary">{taskLabel}</div>
        <Search
          autoFocus
          value={keyword}
          onChange={(v) => {
            setKeyword(v);
            void search(v);
          }}
          placeholder="이름 · 연락처 · 공종 검색..."
          aria-label="시공파트너 검색"
        />
        <div className="flex max-h-72 flex-col gap-03 overflow-y-auto">
          {loading ? (
            <div className="py-06 text-center text-body-sm text-text-muted">검색 중...</div>
          ) : results.length === 0 ? (
            <div className="py-06 text-center text-body-sm text-text-muted">검색 결과 없음</div>
          ) : (
            results.map((p) => (
              <button
                key={p.partnerId}
                type="button"
                onClick={() => onAssign({ partnerId: p.partnerId, partnerName: p.partnerName, mobile: p.mobile })}
                className="flex w-full min-w-0 flex-col items-start gap-01 rounded-lg border border-border px-04 py-03 text-left transition-colors hover:border-group hover:bg-surface-hover"
              >
                <span className="w-full truncate text-body-sm font-bold text-text">{p.partnerName}</span>
                <span className="w-full truncate text-caption text-text-muted">
                  {specialtyLabel(p.specialtyTypes)} · {p.mobile ?? '-'}
                </span>
              </button>
            ))
          )}
        </div>

        {!showNewForm ? (
          <button
            type="button"
            onClick={() => setShowNewForm(true)}
            className="w-full rounded-lg border border-dashed border-border py-03 text-body-sm font-medium text-text-secondary transition-colors hover:border-group hover:text-group"
          >
            + 새 시공파트너 등록
          </button>
        ) : (
          <div className="flex flex-col gap-sm border-t border-border pt-sm">
            {/* 펼치는 버튼이 사라지므로 첫 입력칸으로 포커스를 넘긴다 */}
            <Input width="fill" autoFocus aria-label="이름" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="이름 (예: 정반장)" />
            <Input width="fill" aria-label="연락처" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="연락처 (예: 010-0000-0000)" />
            <Input width="fill" aria-label="전문공종" value={newRole} onChange={(e) => setNewRole(e.target.value)} placeholder="전문공종 (예: 타일/방수)" />
            {/* 입력 영역 전용 액션 — 모달 공통 버튼(footer)이 아니라 이 폼에 붙는다 */}
            <Button variant="primary" size="sm" className="w-full" disabled={submitting} onClick={() => void createAndAssign()}>
              {submitting ? '등록 중…' : '등록 및 지정'}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
